import Button from "components/common/Button";
import ToggleInput from "components/common/InputField/ToggleInput";
import ModalContainer from "components/common/ModalContainer";
import { AgentType } from "enums/AgentTypeEnum";
import { useMergedSearchParams } from "helpers/mergeSearchParams";
import { useAppDispatch, useAppSelector } from "hooks/storeHooks";
import useGetAllAgent from "hooks/workforceHooks/useGetAllAgent";
import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { workforceActions } from "redux/Ldb/actions";

const SalesTargetAgentModal = ({
    open,
    setOpen,
    openAgentModal,
    setOpenAgentModal,
}: {
    open: boolean;
    setOpen: (open: boolean) => void;
    openAgentModal: boolean;
    setOpenAgentModal: (open: boolean) => void;
}) => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [param] = useSearchParams();
    const agent = param.get("agent");
    const updated = param.get("updated") === "true";
    const { updateSearchParams } = useMergedSearchParams();
    const [isActive, setIsActive] = useState(false);
    const [refetch, setRefetch] = useState(false);

    const { updateAgentStatusSuccess } = useAppSelector((state) => state?.workforce);

    const { data: targetAllAgentsQuery, isFetching: isLoadingAgents } = useGetAllAgent({
        agentType: AgentType.TargetAlly,
        refetch,
    });

    const targetAllyAgent = targetAllAgentsQuery?.find(
        (agent) => agent.type === AgentType.TargetAlly
    );

    const handleUpdateAgentStatus = (isActive: boolean) => {
        dispatch(workforceActions.updateAgentStatus(targetAllyAgent?._id, { isActive }));
    };

    useEffect(() => {
        if (targetAllyAgent?.activeAgent?.isActive) {
            setIsActive(true);
        } else {
            setIsActive(false);
        }
    }, [targetAllyAgent]);

    useEffect(() => {
        if (Boolean(updateAgentStatusSuccess)) {
            dispatch(workforceActions.resetUpdateAgentStatusSuccess());
            setRefetch(!refetch);
        }
    }, [dispatch, updateAgentStatusSuccess]);

    useEffect(() => {
        if (
            agent &&
            agent === "TargetAlly" &&
            !updated &&
            targetAllyAgent?.activeAgent?.isActive === true &&
            !isLoadingAgents
        ) {
            setOpen(true);
            setOpenAgentModal(false);
        } else if (
            agent &&
            agent === "TargetAlly" &&
            !updated &&
            !isLoadingAgents &&
            targetAllyAgent
        ) {
            setOpenAgentModal(true);
            setOpen(false);
        }
    }, [agent, targetAllyAgent, updated, isLoadingAgents]);

    return (
        <ModalContainer
            open={openAgentModal}
            showCloseIcon={false}
            tailwindClassName="w-[38%] max-lg:w-1/2 max-sm:w-[95%] !max-h-[90vh] overflow-y-auto"
            closeModal={() => {
                setOpenAgentModal(false);
                updateSearchParams({
                    updated: "true",
                });
            }}
        >
            <div className="bg-white shadow-cardShadow relative rounded-lg font-rocGroteskMedium">
                <div className="w-full md:flex md:text-center items-center md:justify-center">
                    <div className="px-6 w-full flex py-3 border-b border-slate-200 text-center items-center justify-start relative">
                        <span className="text-base font-rocGroteskMedium text-g-75">
                            Enable Required AI Agent
                        </span>
                    </div>
                </div>
                <div className="py-3 px-6 bg-slate-50">
                    <p className="text-sm text-slate-500">
                        To continue, activate the agents needed for this task.This agents help
                        product planning. You can turn these off anytime from the{` `}
                        <span
                            className="underline cursor-pointer text-g-75"
                            onClick={() => {
                                navigate("/dashboard/workforce");
                            }}
                        >
                            WorkforceIQ.
                        </span>
                    </p>
                </div>
                <div className="flex items-center justify-center py-6 ">
                    <div className="cursor-pointer max-w-[325px] shadow-[0px_4px_8px_-4px_rgba(16,24,40,0.12),0px_20px_24px_-4px_rgba(16,24,40,0.08)] rounded-xl bg-white border border-[#E2E8F0]">
                        <div className="relative">
                            <img
                                src={
                                    "https://storage.googleapis.com/heysynth_files/synth_webapp/Frame%202085661197%20(12).svg"
                                }
                                alt={"Target Ally"}
                                className="w-full h-auto rounded-t-xl"
                            />
                        </div>
                        <div className="bg-white rounded-b-xl py-4 px-5 text-sm flex flex-col justify-between">
                            <div>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div className="w-5 h-5 rounded-full flex items-center justify-center bg-[#56B648]">
                                            <i className="ri-robot-fill text-white text-sm"></i>
                                        </div>
                                        <p className="text-base text-g-75">Target Ally</p>
                                    </div>
                                </div>
                                <p className="text-slate-500 text-sm mt-2">
                                    Set and track sales target and performance using natural
                                    language prompts
                                </p>

                                <div className={`flex items-center gap-1 mt-4`}>
                                    <ToggleInput
                                        checked={isActive}
                                        name={"isActive"}
                                        onChange={(e) => {
                                            setIsActive(e.target.checked);
                                            handleUpdateAgentStatus(e.target.checked);
                                        }}
                                        className="before:!h-4 before:!w-4 h-5 w-[30px] before:left-[2px] rounded-[30px] s-slider"
                                    />{" "}
                                    <p className="text-slate-800 -mt-1 text-[13px] font-rocGroteskMedium">
                                        Turn {isActive ? "off" : "on"} agent
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mt-1 py-4 px-6 border-t border-slate-200 flex items-center space-x-2 justify-end">
                    <Button
                        btnText={"Cancel"}
                        onClick={() => {
                            setOpenAgentModal(false);
                        }}
                        btnClassname={"!bg-tradeally-neutral-20 !text-gm-75 !w-fit "}
                    />
                    <Button
                        btnText={"Continue"}
                        disabled={!isActive}
                        type={"submit"}
                        btnClassname="!w-fit"
                        btnTextClassName="!text-md"
                        onClick={() => {
                            setOpenAgentModal(false);
                            setOpen(true);
                            updateSearchParams({
                                updated: "true",
                            });
                        }}
                    />
                </div>
            </div>
        </ModalContainer>
    );
};

export default SalesTargetAgentModal;
